import { useState } from "react";

import type { LibraryItem, LibraryState } from "./useLibrary";

// CO-21 — the standalone AssignSheet's draft state + save, extracted from
// LibraryBrowser (same "stays layout + grouping" reason useLibraryActions
// was split out): ComposerAssigneesBand/AssignSheet is presentational, the
// HOST owns the draft and the PATCH (Maxes/EditMaxSheet's useMaxEditor
// split). One save-error mapper, shared by both kinds — mirrors
// useProgramEditor's own showSaveError (ProgramEditor/useProgramEditor.ts):
// same ApiError.body duck-type (that package can't be imported from either
// host's api client, so both places re-derive the shape by hand), same two
// named causes a coach can act on (a typo'd or unregistered email —
// PE-48). Not centralized: showSaveError is a private hook-local callback,
// not an export, and it picks its own namespace from a different signal
// (single vs prescribe mode) than this sheet has (the item's own kind) — an
// 8-line duck-type isn't worth a cross-owner export to share.
const assigneesSaveErrorMessage = (
  err: Error,
  ns: "program" | "plans",
  t: (key: string, opts?: Record<string, unknown>) => string,
): string => {
  const bodyErr = (err as unknown as { body?: { error?: unknown } } | null)
    ?.body?.error;
  const fallback = t("library.errAssign", {
    defaultValue: "Could not update assignees.",
  });
  if (bodyErr === "invalid_email" ||
        bodyErr === "email_not_found" ||
        bodyErr === "too_many_assignees") {
    return t(`${ns}.errCode.${bodyErr}`, { defaultValue: fallback });
  }
  return fallback;
};

export function useLibraryAssignSheet(
  lib: Pick<LibraryState, "setProgramAssignees" | "setPlanAssignees">,
  callerSub: string | null,
  t: (key: string, opts?: Record<string, unknown>) => string,
) {
  const [assigningItem, setAssigningItem] = useState<LibraryItem | null>(
    null,
  );
  const [assignedToDraft, setAssignedToDraft] = useState<string[]>([]);
  const [assigneeEmailsDraft, setAssigneeEmailsDraft] = useState<string[]>(
    [],
  );
  const [assignBusy, setAssignBusy] = useState(false);
  const [assignError, setAssignError] = useState<string | null>(null);
  // CO-22 — a truthful post-save confirmation. The sheet closes on
  // success (below), so this outlives it the same way useLibrary's own
  // `cloneFlash` does: rendered by the host AFTER the sheet is gone, not
  // inside it.
  const [assignFlash, setAssignFlash] = useState<string | null>(null);

  const onAssign = (item: LibraryItem) => {
    if (item.source !== "personal") return;
    const current =
      item.kind === "program"
        ? item.program.assignedTo
        : item.plan.assignedTo;
    // Mirrors programEdit.ts's own draft-load: the "" sentinel a never-
    // shared row's assignedTo can carry is filtered here too, and pending
    // (not yet accepted) email invites aren't read back either — the same
    // limitation the full editor already has, not a new one this sheet
    // introduces.
    setAssignedToDraft(current.filter((s) => s !== ""));
    setAssigneeEmailsDraft([]);
    setAssignError(null);
    setAssignFlash(null);
    setAssigningItem(item);
  };

  const closeAssignSheet = () => {
    if (assignBusy) return;
    setAssigningItem(null);
  };

  const onSaveAssignees = async () => {
    if (!assigningItem || assigningItem.source !== "personal") return;
    setAssignBusy(true);
    setAssignError(null);
    // The self row is never removable in the band's UI, but it isn't
    // necessarily IN `assignedTo` yet (PE-48: self is a display-only fake
    // until the first real add) — and the server's /assignees PATCH
    // requires at least one sub. Folding the caller in here means the UI
    // never manufactures the 400 it never let the coach create in the
    // first place.
    const assignedTo = callerSub
      ? [...new Set([callerSub, ...assignedToDraft])]
      : assignedToDraft;
    const body = {
      assignedTo,
      ...(assigneeEmailsDraft.length > 0
        ? { assigneeEmails: assigneeEmailsDraft }
        : {}),
    };
    const err =
      assigningItem.kind === "program"
        ? await lib.setProgramAssignees(assigningItem.program, body)
        : await lib.setPlanAssignees(assigningItem.plan, body);
    setAssignBusy(false);
    if (err) {
      setAssignError(
        assigneesSaveErrorMessage(
          err,
          assigningItem.kind === "program" ? "program" : "plans",
          t,
        ),
      );
      return;
    }
    // CO-22 — this PATCH goes through proposeAssignments same as every
    // other assign path: it PROPOSES, new names on the list get a
    // refusable offer, not an instant grant. `authoredAs === "coach"`
    // is also exactly proposeAssignments' own skip condition mirrored
    // client-side — self is folded into `assignedTo` on every save here
    // (the merge a few lines up), and for a coach-authored item that
    // self-inclusion is a real proposal target too (CO-22's filter only
    // exempts a self-authored self-assign). So whenever this item is
    // coach-authored, the caller's own save also reaches their own
    // athlete inbox — worth saying, since `audience: "athlete"` means it
    // won't show up under the coach view they're looking at right now.
    const itemAuthoredAs =
      assigningItem.kind === "program"
        ? assigningItem.program.authoredAs
        : assigningItem.plan.authoredAs;
    setAssignFlash(
      itemAuthoredAs === "coach"
        ? t("library.assignSentSelf", {
            defaultValue:
              "Sent — since this is coach-authored, check your own athlete inbox to accept or decline it too.",
          })
        : t("library.assignSent", {
            defaultValue:
              "Sent — everyone new on the list gets a request they can accept or decline.",
          }),
    );
    setAssigningItem(null);
  };

  return {
    assigningItem,
    assignedToDraft,
    assigneeEmailsDraft,
    setAssignedToDraft,
    setAssigneeEmailsDraft,
    assignBusy,
    assignError,
    assignFlash,
    onAssign,
    closeAssignSheet,
    onSaveAssignees,
  };
}
